import { ISharedWishlistPageContent } from 'pages/shared-list';
import ProductsSearchResult from '@components/ProductsSearchResult';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { useTranslations } from 'next-intl';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import Cart from '@monofront/design-system/visuals/Icons/Cart';
import { toast } from 'react-toastify';
import logger from '@utils/logger';
import { useShoppingCart, useSideCart } from '@hooks/cart/useShoppingCart';
import { IProduct, ISearchProduct } from '@monofront/types';
import { format as formatPrice } from '@monofront/business/utils';
import ClientOnlyPortal from '@components/ClientOnlyPortal';
import { checkAddressSource } from '@context/shoppingCart';
import { useEligibility } from '@monofront/business/contexts';
import { useConfigure, useHits } from 'react-instantsearch-core';

type SharedWishlistProps = {
  pageContent: ISharedWishlistPageContent;
  title: string | null;
  owner: string | null;
  skus: Array<string>;
};

const SharedWishlist: React.FC<SharedWishlistProps> = ({
  pageContent,
  title,
  owner,
  skus,
}) => {
  const tWishlists = useTranslations('Wishlists');
  const tCart = useTranslations('Cart');
  const tErrors = useTranslations('Errors');
  const skusArray = typeof skus !== 'string' ? (skus as string[]) : [skus];
  const filters = `sku:${skusArray.join(' OR sku:')}`;
  const { shoppingCartId, addProductsToShoppingCart } = useShoppingCart();
  const { checkNeedEligibility } = useEligibility();
  const { hits } = useHits<IProduct>();
  const totalPrice = hits?.reduce(
    (accumulator, { price }) => price + accumulator,
    0,
  );
  const totalComparedPrice = hits?.reduce(
    (accumulator, { compare_at_price }) => compare_at_price + accumulator,
    0,
  );
  const { dispatchSideCart } = useSideCart();

  useConfigure({ filters });

  const addHitsProductsToShoppingCart = async (
    shoppingCartId: string,
    hits: ISearchProduct[],
  ) => {
    const productsToAdd = hits
      .filter((product) => product.inventory_quantity > 0)
      .map((product) => ({
        ...product,
        quantity: 1,
      }));

    await addProductsToShoppingCart(shoppingCartId, productsToAdd);
  };

  const addWishlistProductsToShoppingCart = async () => {
    try {
      if (!shoppingCartId) {
        throw new Error('Unexpected empty `shoppingCartId`');
      }

      if (hits?.length > 0) {
        const shouldCheckFreshAddress = hits.some(checkNeedEligibility);

        if (shouldCheckFreshAddress) {
          dispatchSideCart({
            type: 'checkAddress',
            source: checkAddressSource.DEFAULT,
            addFreshProductsToCart: async () => {
              await addHitsProductsToShoppingCart(shoppingCartId, hits);
            },
          });

          return;
        }

        await addHitsProductsToShoppingCart(shoppingCartId, hits);
      }

      toast(tCart('productsAddedToCart'));
    } catch (error: unknown) {
      logger.error(error);
      toast(tErrors('addProductsToCartError'), {
        progressClassName: 'toast-error',
      });
    }
  };

  return (
    <>
      <section className="limited-width-container tw-mb-3 desktop:tw-mb-12">
        <div className="tw-mb-1 tw-flex tw-overflow-hidden tw-bg-beige-200 tw-p-6 desktop:tw-my-6 desktop:tw-h-[140px] desktop:tw-rounded-2xl desktop:tw-p-0">
          <div className="tw-relative tw-ml-10 tw-mr-[40px] tw-hidden tw-h-[150px] tw-w-[87px] desktop:tw-block">
            <div className="tw-absolute tw-mt-3">
              <ResponsiveImage
                src={pageContent.shareMyListBannerImage.url}
                width={87}
                height={140}
              />
            </div>
          </div>
          <div className="tw-flex tw-flex-col tw-justify-center">
            {title && (
              <h1
                className="tw-lf-title-md tw-mb-2 desktop:tw-lf-title-lg"
                data-testid="SharedWishlist-title"
              >
                {title}
              </h1>
            )}
            {owner && (
              <p className="tw-lf-body-bold-md tw-lf-ellipse-container">
                <span data-testid="SharedWishlist-owner">
                  {tWishlists('listOf', {
                    owner,
                  })}
                </span>
                <span className="tw-lf-ellipse"></span>
                <span data-testid="SharedWishlist-nbProducts">
                  {tWishlists('nbProducts', {
                    nb: skusArray.length,
                  })}
                </span>
              </p>
            )}
          </div>
        </div>
        <ProductsSearchResult withSortFilterOnly withTotalHits={false} />
      </section>
      <ClientOnlyPortal selector="body">
        <div className="tw-sticky !tw-bottom-0 tw-z-sticky tw-border-0 tw-border-t tw-border-solid tw-border-t-blue-200 tw-bg-white tw-px-6 desktop:tw-px-0">
          <div className="limited-width-container tw-flex tw-h-[75px] tw-items-center tw-justify-between">
            <p className="tw-flex tw-flex-col tw-items-center">
              <span className="tw-lf-title-md">{formatPrice(totalPrice)}</span>
              <span className="tw-lf-body-regular-md tw-line-through">
                {formatPrice(totalComparedPrice)}
              </span>
            </p>
            <button
              data-testid="SharedWishlist-add-to-cart"
              className="tw-lf-button tw-lf-button-conversion tw-lf-button-md"
              onClick={() => {
                addWishlistProductsToShoppingCart();
              }}
            >
              <Cart size={IconSizes.md} />
              {pageContent.addToCartButtonLabel}
            </button>
          </div>
        </div>
      </ClientOnlyPortal>
    </>
  );
};

export default SharedWishlist;
